// TemporaryOperatingInstruction.tsx
import { Calendar, Pencil, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader/Loader';
import RejectButton from '../../../components/RejectButton/RejectButton';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';
import TemporaryOperatingInstructionModal from './TemporaryOperatingInstructionModal.tsx';
import {
  approveExtension,
  markAccepted,
  markAuthorized,
  markReviewed,
  markSupported,
  notifyAcceptance,
  notifyAuthorize,
  notifyReview,
  notifySupport,
  rejectExtension,
  removeTOI,
  requestExtension,
} from '../../../services/tempAPIService';
import {
  deleteTempOpInstruction,
  getOperationsManagers,
  getSectionHead,
  getShiftSupervisors,
  getSystemEngineers,
  getTechnicalManagers,
  getTempOpInstructionDetail,
  getTOIDownload,
  markInstructionAdd,
  notifySystemEngineer,
} from '../../../services/temporaryOpInstructionService';

interface TOIData {
  id: number | string;
  reference_number: string;
  title: string;
  description: string | null;
  site: string | null;
  valid_from: string | null;
  valid_to: string | null;
  toi_number: string | null;
  unit_station: string | null;
  system: string | null;
  doc_type: string | null;
  form_number: string | null;
  serial_number: string | null;
  rev: string | null;
  hbs: string | null;
  reason_for_temporary_instruction: string | null;
  temporary_instruction: string[] | string | null;
  created_by: string;
  status: string | null;
  created_at: string;
  'status-details': string | null;

  workflow_stages?: any[];
  extensions?: {
    id: number;
    status: string;
    requested_end_date?: string | null;
    approved_by?: {
      id: number;
      name: string;
    };
  }[];
}

const stripHtml = (text: string) => text.replace(/<[^>]*>/g, '');

interface Supervisor {
  id: number;
  name: string;
  avatarUrl?: string | null;
}

interface SupervisorMultiSelectProps {
  supervisors: Supervisor[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

const SupervisorMultiSelect: React.FC<SupervisorMultiSelectProps> = ({ supervisors, selectedIds, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = supervisors.filter((s) => selectedIds.includes(s.id));
  const options = supervisors.filter((s) => !selectedIds.includes(s.id) && s.name.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addSupervisor = (id: number) => {
    onChange([...selectedIds, id]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeSupervisor = (id: number) => {
    onChange(selectedIds.filter((sid) => sid !== id));
  };

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
        className="w-full min-h-[52px] border border-gray-300 rounded-md px-3 py-2 flex flex-wrap items-center gap-2 cursor-text bg-white focus-within:ring-2"
      >
        {selected.map((supervisor) => (
          <span key={supervisor.id} className="flex items-center gap-2 bg-gray-100 rounded-full pl-1 pr-2 py-1 text-sm text-gray-800">
            {supervisor.avatarUrl ? (
              <img src={supervisor.avatarUrl} alt={supervisor.name} className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <span className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                {supervisor.name.charAt(0)}
              </span>
            )}
            {supervisor.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeSupervisor(supervisor.id);
              }}
              className="text-gray-400 hover:text-gray-600 ml-1"
            >
              ×
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={selected.length === 0 ? 'Add Shift Supervisors' : ''}
          className="flex-1 min-w-[120px] outline-none text-sm py-1"
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          className="text-gray-400 hover:text-gray-600 ml-auto"
        >
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[220px] overflow-y-auto">
          {options.length > 0 ? (
            options.map((supervisor) => (
              <button
                key={supervisor.id}
                type="button"
                onClick={() => addSupervisor(supervisor.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left"
              >
                {supervisor.avatarUrl ? (
                  <img src={supervisor.avatarUrl} alt={supervisor.name} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                    {supervisor.name.charAt(0)}
                  </span>
                )}
                <span className="text-gray-800 text-sm">{supervisor.name}</span>
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-400">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};

const TempOperatingInstructionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TOIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit / Delete (New status only)
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 🔹 MODAL STATE
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [systemEngineer, setSystemEngineer] = useState([]);
  const [selectedSystemEngineer, setSelectedSystemEngineer] = useState('');

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [sectionHeads, setSectionHeads] = useState<any[]>([]);
  const [selectedSectionHead, setSelectedSectionHead] = useState('');
  const [loadingSectionHeads, setLoadingSectionHeads] = useState(false);

  const [tempOperatingInstructionModal, setTempOperatingInstructionModal] = useState(false);

  const [newInstruction, setNewInstruction] = useState('');
  const [addInstructionLoading, setAddInstructionLoading] = useState(false);

  const [showReviewedModal, setShowReviewedModal] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  const [showNotifyTechManagerModal, setShowNotifyTechManagerModal] = useState(false);
  const [techManagers, setTechManagers] = useState<any[]>([]);
  const [selectedTechManager, setSelectedTechManager] = useState('');
  const [loadingTechManagers, setLoadingTechManagers] = useState(false);

  const [showSupportModal, setShowSupportModal] = useState(false);
  const [submittingSupport, setSubmittingSupport] = useState(false);

  const [showNotifyOperationsManagerModal, setShowNotifyOperationsManagerModal] = useState(false);
  const [operationsManagers, setOperationsManagers] = useState<any[]>([]);
  const [selectedOperationsManager, setSelectedOperationsManager] = useState('');
  const [loadingOperationsManagers, setLoadingOperationsManagers] = useState(false);

  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false);
  const [submittingAuthorize, setSubmittingAuthorize] = useState(false);

  const [showNotifyShiftSupervisorModal, setShowNotifyShiftSupervisorModal] = useState(false);
  const [shiftSupervisors, setShiftSupervisors] = useState<any[]>([]);
  const [selectedShiftSupervisors, setSelectedShiftSupervisors] = useState<number[]>([]);
  const [loadingShiftSupervisors, setLoadingShiftSupervisors] = useState(false);

  const [isNotifyingSystemEngineer, setIsNotifyingSystemEngineer] = useState(false);
  const [isNotifyingSectionHead, setIsNotifyingSectionHead] = useState(false);
  const [isNotifyingTechManager, setIsNotifyingTechManager] = useState(false);
  const [isNotifyingOperationsManager, setIsNotifyingOperationsManager] = useState(false);
  const [isNotifyingShiftSupervisors, setIsNotifyingShiftSupervisors] = useState(false);
  const [isRequestingExtension, setIsRequestingExtension] = useState(false);

  const [showAcceptTOIModal, setShowAcceptTOIModal] = useState(false);
  const [acceptTOILoading, setAcceptTOILoading] = useState(false);

  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [technicalManagers, setTechnicalManagers] = useState<any[]>([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [extendTo, setExtendTo] = useState('');
  const [loadingManagers, setLoadingManagers] = useState(false);

  const [showApproveExtensionModal, setShowApproveExtensionModal] = useState(false);
  const [approveExtensionLoading, setApproveExtensionLoading] = useState(false);
  const [showRejectExtensionModal, setShowRejectExtensionModal] = useState(false);

  const [showRemoveTOIModal, setShowRemoveTOIModal] = useState(false);
  const [removingTOI, setRemovingTOI] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const currentUserID = Number(localStorage.getItem('currentUserID'));

  const [downloadLoading, setDownloadLoading] = useState(false);

  const isPendingApprover = (stageName: string) =>
    data?.workflow_stages?.some(
      (stage: any) =>
        stage.stage === stageName &&
        stage.approvers?.some((approver: any) => Number(approver.user?.id) === Number(currentUserID) && approver.status === 'PENDING'),
    ) ?? false;

  const canReview = isPendingApprover('REVIEW');
  const canSupport = isPendingApprover('SUPPORT');
  const canAuthorize = isPendingApprover('AUTHORIZE');
  const canAccept = isPendingApprover('ACCEPT');
  const canAddInstruction = isPendingApprover('INSTRUCTION_ADD');
  // Pulls the assigned-approver user id off an extension regardless of which
  // field name the API actually uses for it (approved_by, approver,
  // assigned_to, etc. all show up across similar workflow endpoints).
  const getExtensionApproverId = (extension: any): number | null => {
    const raw =
      extension?.approved_by?.id ??
      extension?.approver?.id ??
      extension?.approved_by_id ??
      extension?.approver_id ??
      extension?.assigned_to?.id ??
      extension?.assigned_to_id ??
      null;
    return raw === null || raw === undefined ? null : Number(raw);
  };

  // Case/format-insensitive PENDING check — API may send 'Pending', 'pending',
  // or a differently-cased constant instead of the exact string 'PENDING'.
  const isPendingStatus = (status: any) => typeof status === 'string' && status.trim().toUpperCase() === 'PENDING';

  const canApproveExtension =
    data?.extensions?.some(
      (extension) => getExtensionApproverId(extension) === Number(currentUserID) && isPendingStatus(extension.status),
    ) ?? false;

  // Status-agnostic check: was this user ever the assigned approver on any
  // extension for this TOI? Unlike canApproveExtension, this stays true after
  // the extension has been approved/rejected (status is no longer PENDING),
  // so the approver keeps Download access on the resolved statuses too.
  const isExtensionApprover = data?.extensions?.some((extension) => getExtensionApproverId(extension) === Number(currentUserID)) ?? false;

  // TEMP DEBUG: confirm the real shape of extensions + which check is failing.
  // Remove once the approver-visibility issue is confirmed fixed.
  if (data?.extensions?.length) {
    console.log('[TOI debug] currentUserID:', currentUserID);
    console.log('[TOI debug] extensions:', JSON.stringify(data.extensions, null, 2));
    console.log('[TOI debug] canApproveExtension:', canApproveExtension, 'isExtensionApprover:', isExtensionApprover);
  }

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const result = await getTempOpInstructionDetail(id);

        if (result.temporary_instruction && typeof result.temporary_instruction === 'string') {
          result.temporary_instruction = [result.temporary_instruction];
        }

        setData(result);
      } catch {
        setError('Failed to fetch data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDownload = async () => {
    if (!data?.id) return;

    try {
      setDownloadLoading(true);

      const blob = await getTOIDownload(data.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TOI_${data.reference_number || data.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to download TOI.';
      showErrorToast(message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleOpenReviewModal = async () => {
    try {
      setLoadingSectionHeads(true);

      const response = await getSectionHead();

      setSectionHeads(response.data || response);
      setShowReviewModal(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSectionHeads(false);
    }
  };

  const handleOpenNotifyOperationsManagerModal = async () => {
    try {
      setLoadingOperationsManagers(true);

      const response = await getOperationsManagers();

      setOperationsManagers(response.data || response);

      setShowNotifyOperationsManagerModal(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOperationsManagers(false);
    }
  };

  const handleNotifyAuthorize = async () => {
    if (!selectedOperationsManager) {
      alert('Please select an Operations Manager');
      return;
    }

    try {
      setIsNotifyingOperationsManager(true);
      await notifyAuthorize(id, {
        user_ids: [Number(selectedOperationsManager)],
      });

      setShowNotifyOperationsManagerModal(false);

      showSuccessToast('Notification successfully sent to the Operations Manager.');

      setSelectedOperationsManager('');

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setIsNotifyingOperationsManager(false);
    }
  };

  const handleNotifyReview = async () => {
    if (!selectedSectionHead) {
      alert('Please select a Section Head');
      return;
    }

    try {
      setIsNotifyingSectionHead(true);
      await notifyReview(id, {
        user_ids: [Number(selectedSectionHead)],
      });

      setShowReviewModal(false);
      setSelectedSectionHead('');

      showSuccessToast('Notification successfully sent to the Section Head for review.');
      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setIsNotifyingSectionHead(false);
    }
  };

  const handleOpenNotifyTechManagerModal = async () => {
    try {
      setLoadingTechManagers(true);

      const response = await getTechnicalManagers();

      setTechManagers(response.data || response);

      setShowNotifyTechManagerModal(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTechManagers(false);
    }
  };

  const handleNotifySupport = async () => {
    if (!selectedTechManager) {
      alert('Please select a Technical Manager');
      return;
    }

    try {
      setIsNotifyingTechManager(true);
      await notifySupport(id, {
        user_ids: [Number(selectedTechManager)],
      });

      setShowNotifyTechManagerModal(false);

      showSuccessToast('Notification successfully sent to the Technical Manager.');

      setSelectedTechManager('');

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setIsNotifyingTechManager(false);
    }
  };

  const handleAddInstruction = async () => {
    try {
      if (!newInstruction.trim()) {
        alert('Please enter a temporary instruction');
        return;
      }

      setAddInstructionLoading(true);

      await markInstructionAdd(id!, {
        temporary_instruction: newInstruction,
      });

      setTempOperatingInstructionModal(false);
      setNewInstruction('');

      // Refresh page data
      const result = await getTempOpInstructionDetail(id!);

      if (result.temporary_instruction && typeof result.temporary_instruction === 'string') {
        result.temporary_instruction = [result.temporary_instruction];
      }

      setData(result);
      fetchTOIDetails();
    } catch (error) {
      console.error('Failed to add instruction:', error);
      alert('Failed to add instruction');
    } finally {
      setAddInstructionLoading(false);
    }
  };

  const handleNotifySystemEngineerClick = async () => {
    try {
      const res = await getSystemEngineers();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setSystemEngineer(users);
      fetchTOIDetails();
    } catch (error) {
      console.error('Failed to fetch Shift Supervisors:', error);
    }
  };

  useEffect(() => {
    handleNotifySystemEngineerClick();
  }, []);

  const fetchTOIDetails = async () => {
    try {
      setLoading(true);

      const result = await getTempOpInstructionDetail(id!);

      if (result.temporary_instruction && typeof result.temporary_instruction === 'string') {
        result.temporary_instruction = [result.temporary_instruction];
      }

      setData(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (id) {
      fetchTOIDetails();
    }
  }, [id]);

  if (loading) return <Loader />;
  if (error) return <div className="text-center py-10 text-red-500">{error}</div>;
  if (!data) return <div className="text-center py-10">No data found</div>;

  const instructions: string[] = Array.isArray(data.temporary_instruction)
    ? data.temporary_instruction
    : data.temporary_instruction
      ? [data.temporary_instruction]
      : [];

  const handleSelectedSEngineerConfirm = async () => {
    try {
      if (!selectedSystemEngineer) {
        alert('Please select a System Engineer');
        return;
      }
      setIsNotifyingSystemEngineer(true);
      await notifySystemEngineer(id!, {
        user_ids: [Number(selectedSystemEngineer)],
      });

      setShowNotifyModal(false);
      showSuccessToast('Notification successfully sent to the System Engineer.');

      fetchTOIDetails();
    } catch (error) {
      console.error('System Engineer notification failed:', error);
    } finally {
      setIsNotifyingSystemEngineer(false);
    }
  };

  const handleMarkReviewed = async () => {
    try {
      setSubmittingReview(true);

      await markReviewed(id);

      setShowReviewedModal(false);

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleMarkSupported = async () => {
    try {
      setSubmittingSupport(true);

      await markSupported(id);

      setShowSupportModal(false);

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmittingSupport(false);
    }
  };

  const handleMarkAuthorized = async () => {
    try {
      setSubmittingAuthorize(true);

      await markAuthorized(id);

      setShowAuthorizeModal(false);

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmittingAuthorize(false);
    }
  };

  const handleOpenNotifyShiftSupervisorModal = async () => {
    try {
      setLoadingShiftSupervisors(true);
      const response = await getShiftSupervisors();
      const raw = response.data || response;

      const mapped = raw.map((s: any) => ({
        id: s.id,
        name: s.name || s.full_name,
        avatarUrl: s.photo_url || s.avatar || s.profile_image || null,
      }));

      setShiftSupervisors(mapped);
      setShowNotifyShiftSupervisorModal(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingShiftSupervisors(false);
    }
  };

  const handleNotifyShiftSupervisors = async () => {
    if (selectedShiftSupervisors.length === 0) {
      alert('Please select at least one Shift Supervisor');
      return;
    }

    try {
      setIsNotifyingShiftSupervisors(true);
      await notifyAcceptance(id, {
        user_ids: selectedShiftSupervisors,
      });

      setShowNotifyShiftSupervisorModal(false);
      showSuccessToast('Notification successfully sent to the Shift Supervisor.');
      setSelectedShiftSupervisors([]);

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setIsNotifyingShiftSupervisors(false);
    }
  };

  const handleAcceptTOI = async () => {
    try {
      setAcceptTOILoading(true);

      await markAccepted(id);

      setShowAcceptTOIModal(false);

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setAcceptTOILoading(false);
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'New':
        return 'text-blue-500';

      case 'Pending Instruction Addition':
        return 'text-orange-500';

      case 'Instructions Added':
        return 'text-[#6e35e8]';

      case 'Review Pending':
        return 'text-[#ec8b32]';

      case 'Reviewed':
        return 'text-[#d956ee]';

      case 'Pending Support':
        return 'text-[#d956ee]';

      case 'Supported':
        return 'text-[#ff4285]';

      case 'Pending Authorization':
        return 'text-[#ff4285]';

      case 'Authorized':
        return 'text-[#ec8b32]';

      case 'Acceptence Pending':
        return 'text-[#ff7f87]';

      case 'Accepted':
        return 'text-[#ff7f87]';

      case 'Extension Pending Approval':
        return 'text-orange-700';

      case 'Extension Approved':
        return 'text-teal-600';

      case 'Extension Rejected':
        return 'text-red-600';

      case 'Removed':
        return 'text-red-700';

      case 'Expired':
        return 'text-red-600';

      case 'Cancelled':
        return 'text-slate-500';

      default:
        return 'text-[#007c17]';
    }
  };

  const loadTechnicalManagers = async () => {
    try {
      setLoadingManagers(true);

      const response = await getTechnicalManagers();

      setTechnicalManagers(response.data || response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingManagers(false);
    }
  };

  // Returns yyyy-mm-dd for the day immediately after the given date,
  // suitable for both the <input type="date"> value and its min attribute.
  // Falls back to tomorrow's date if the input can't be parsed.
  const getNextDay = (dateString: string | null | undefined) => {
    const parsed = dateString ? new Date(dateString) : new Date();
    const base = isNaN(parsed.getTime()) ? new Date() : parsed;

    base.setDate(base.getDate() + 1);
    return base.toISOString().split('T')[0];
  };

  const handleOpenExtensionModal = () => {
    setShowExtensionModal(true);
    setExtendTo('');
    loadTechnicalManagers();
  };

  const handleRequestExtension = async () => {
    try {
      setIsRequestingExtension(true);
      const payload = {
        technical_manager_id: selectedManager,
        requested_end_date: extendTo,
      };

      await requestExtension(data.id, payload);

      setShowExtensionModal(false);
      setSelectedManager('');
      setExtendTo('');

      fetchTOIDetails?.();
    } catch (error: any) {
      console.error(error);

      const message =
        error?.response?.data?.message || error?.response?.data?.errors?.requested_end_date?.[0] || 'Failed to submit extension request';

      showErrorToast(message);
    }
  };

  const handleApproveExtension = async () => {
    try {
      const pendingExtension = data?.extensions?.find(
        (ext) => Number(ext.approved_by?.id) === Number(currentUserID) && ext.status === 'PENDING',
      );

      if (!pendingExtension) return;

      setApproveExtensionLoading(true);
      await approveExtension(pendingExtension.id);

      setShowApproveExtensionModal(false);
      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setApproveExtensionLoading(false);
    }
  };

  const handleRejectExtension = async () => {
    try {
      const pendingExtension = data?.extensions?.find(
        (ext) => Number(ext.approved_by?.id) === Number(currentUserID) && ext.status === 'PENDING',
      );

      if (!pendingExtension) return;

      await rejectExtension(pendingExtension.id);

      setShowRejectExtensionModal(false);
      fetchTOIDetails();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveTOI = async () => {
    try {
      setRemovingTOI(true);

      await removeTOI(data.id);

      setShowRemoveTOIModal(false);

      fetchTOIDetails();
    } catch (error) {
      console.error(error);
      alert('Failed to revoke TOI');
    } finally {
      setRemovingTOI(false);
    }
  };

  const handleDeleteTOI = async () => {
    if (!data) return;
    try {
      setDeleteLoading(true);
      await deleteTempOpInstruction(data.id);
      setShowDeleteModal(false);
      // Instruction no longer exists — go back to the list
      navigate('/TemporaryOperatingInstruction');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete instruction.';
      showErrorToast(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const showSuccessToast = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);

    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  const showErrorToast = (message: string) => {
    setErrorMessage(message);
    setShowErrorMessage(true);

    setTimeout(() => {
      setShowErrorMessage(false);
    }, 4000);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB');
  };
  const Spinner = () => (
    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );

  return (
    <div className="relative w-full bg-white min-h-screen">
      {/* CONTENT */}
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-6 lg:pr-[300px]">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold">{data.reference_number}</h2>

              {data.status && (
                <>
                  <span className={`text-sm ${getStatusColor(data.status)}`}>● {data.status}</span>

                  {data.status === 'Extension Pending Approval' &&
                    (() => {
                      const pendingExtension = data.extensions?.find(
                        (ext) => Number(ext.approved_by?.id) === Number(currentUserID) && ext.status === 'PENDING',
                      );

                      return (
                        <span className="text-sm text-orange-600 font-medium">
                          ({formatDate(data.valid_to)} to{' '}
                          {pendingExtension?.requested_end_date ? formatDate(pendingExtension.requested_end_date) : '-'})
                        </span>
                      );
                    })()}
                </>
              )}
            </div>

            {(data.valid_from || data.valid_to) && (
              <div className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                {/* Start Date with Calendar icon */}
                {data.valid_from && (
                  <span className="flex items-center gap-1">
                    <Calendar size={14} className="text-gray-400" />
                    <span className="text-gray-700">{formatDate(data.valid_from)}</span>
                  </span>
                )}

                {data.valid_from && data.valid_to && <span className="text-gray-400">to</span>}

                {/* End Date - no icon */}
                {data.valid_to && <span className="text-gray-700">{formatDate(data.valid_to)}</span>}

                {/* Remaining Days */}
                {data.valid_to &&
                  (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const expiry = new Date(data.valid_to);
                    expiry.setHours(0, 0, 0, 0);
                    const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    if (diffDays <= 0) return null;
                    return <span className="text-red-500 font-semibold">(Expires in {diffDays} days)</span>;
                  })()}
              </div>
            )}
          </div>

          {/*  ACCEPT BUTTON + EDIT/DELETE (New status, creator only — including post-rejection reset back to New) */}
          {Number(data.created_by) === currentUserID && data.status === 'New' && (
            <div className="flex items-center gap-3 absolute top-4 right-[-10px]">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 rounded-md border border-gray-300 text-red-500 hover:bg-red-50"
                aria-label="Delete instruction"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="p-2 rounded-md border border-gray-300 text-blue-500 hover:bg-blue-50"
                aria-label="Edit instruction"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowNotifyModal(true)}
                className="bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
              >
                Notify System Engineer
              </button>
            </div>
          )}
          {canAddInstruction && data.status === 'Pending Instruction Addition' && (
            <div className="flex items-center  gap-3 absolute top-4 right-[-10px]">
              <RejectButton
                title="Temporary Operating Instruction"
                id={id!}
                apiPath="temporary-operating-instructions"
                onSuccess={fetchTOIDetails}
              />
              <button
                onClick={() => setTempOperatingInstructionModal(true)}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 whitespace-nowrap"
              >
                Add Temporary Operating Instruction
              </button>
            </div>
          )}

          {Number(data.created_by) === currentUserID && data.status === 'Instructions Added' && (
            <button
              onClick={handleOpenReviewModal}
              className="absolute top-4 right-[-10px] bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
            >
              Notify Section Head
            </button>
          )}

          {canReview && data.status === 'Review Pending' && (
            <div className="flex items-center justify-end gap-3 absolute top-4 right-[-10px]">
              <RejectButton
                title="Temporary Operating Instruction"
                id={id!}
                apiPath="temporary-operating-instructions"
                onSuccess={fetchTOIDetails}
              />
              <button
                onClick={() => setShowReviewedModal(true)}
                className=" bg-red-600 text-white px-10 py-2 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
              >
                Mark As Reviewed
              </button>
            </div>
          )}

          {Number(data.created_by) === currentUserID && data.status === 'Reviewed' && (
            <button
              onClick={handleOpenNotifyTechManagerModal}
              className="absolute top-4 right-[-10px] bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
            >
              Notify Technical Manager
            </button>
          )}

          {canSupport && data.status === 'Pending Support' && (
            <div className="flex items-center justify-end gap-3 absolute top-4 right-[-10px]">
              <RejectButton
                title="Temporary Operating Instruction"
                id={id!}
                apiPath="temporary-operating-instructions"
                onSuccess={fetchTOIDetails}
              />
              <button
                onClick={() => setShowSupportModal(true)}
                className=" bg-red-600 text-white px-10 py-2 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
              >
                Support TOI
              </button>
            </div>
          )}

          {Number(data.created_by) === currentUserID && data.status === 'Supported' && (
            <button
              onClick={handleOpenNotifyOperationsManagerModal}
              className="absolute top-4 right-[-10px] bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
            >
              Notify Operations Manager
            </button>
          )}

          {canAuthorize && data.status === 'Pending Authorization' && (
            <div className="flex items-center justify-end gap-3 absolute top-4 right-[-10px]">
              <RejectButton
                title="Temporary Operating Instruction"
                id={id!}
                apiPath="temporary-operating-instructions"
                onSuccess={fetchTOIDetails}
              />
              <button
                onClick={() => setShowAuthorizeModal(true)}
                className=" bg-red-600 text-white px-10 py-2 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
              >
                Authorize TOI
              </button>
            </div>
          )}

          {Number(data.created_by) === currentUserID && data.status === 'Authorized' && (
            <button
              onClick={handleOpenNotifyShiftSupervisorModal}
              className="absolute top-4 right-[-10px] bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
            >
              Notify Shift Supervisors
            </button>
          )}

          {canAccept && data.status === 'Acceptence Pending' && (
            <div className="flex items-center justify-end gap-3 absolute top-4 right-[-10px]">
              <RejectButton
                title="Temporary Operating Instruction"
                id={id!}
                apiPath="temporary-operating-instructions"
                onSuccess={fetchTOIDetails}
              />
              <button
                onClick={() => setShowAcceptTOIModal(true)}
                className=" bg-red-600 text-white px-10 py-2 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
              >
                Accept TOI
              </button>
            </div>
          )}

          {Number(data.created_by) === currentUserID && data.status === 'Accepted' && (
            <div className="absolute top-4 right-[-150px] flex gap-3">
              <button
                onClick={handleDownload}
                disabled={downloadLoading}
                className="bg-gray-700 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-gray-800 transition disabled:opacity-60"
              >
                {downloadLoading ? 'Downloading...' : 'Download'}
              </button>

              <button
                onClick={() => setShowRemoveTOIModal(true)}
                className="bg-yellow-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-yellow-700 transition"
              >
                Revoke TOI
              </button>

              <button
                onClick={handleOpenExtensionModal}
                className="bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-blue-700 transition"
              >
                Apply for TOI Extension
              </button>
            </div>
          )}

          {/* Approver view: Download available alongside Approve/Decline during extension review */}
          {canApproveExtension &&
            data.status === 'Extension Pending Approval' &&
            (() => {
              const pendingExtension = data?.extensions?.find(
                (ext) => Number(ext.approved_by?.id) === Number(currentUserID) && ext.status === 'PENDING',
              );
              return (
                <div className="absolute top-4 right-[-150px] flex gap-3">
                  {Number(data.created_by) === currentUserID && (
                    <button
                      onClick={handleDownload}
                      disabled={downloadLoading}
                      className="bg-gray-700 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-gray-800 transition disabled:opacity-60"
                    >
                      {downloadLoading ? 'Downloading...' : 'Download'}
                    </button>
                  )}
                  <button
                    onClick={() => setShowRejectExtensionModal(true)}
                    className="bg-yellow-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-yellow-700 transition"
                  >
                    Decline
                  </button>

                  <button
                    onClick={() => setShowApproveExtensionModal(true)}
                    className="bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition"
                  >
                    Approve
                  </button>
                </div>
              );
            })()}

          {/* Creator view: Download available while waiting for someone else to approve/reject the extension */}
          {Number(data.created_by) === currentUserID && data.status === 'Extension Pending Approval' && !canApproveExtension && (
            <button
              onClick={handleDownload}
              disabled={downloadLoading}
              className="absolute top-4 right-[-150px] bg-gray-700 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-gray-800 transition disabled:opacity-60"
            >
              {downloadLoading ? 'Downloading...' : 'Download'}
            </button>
          )}

          {(Number(data.created_by) === currentUserID || isExtensionApprover) &&
            (data.status === 'Extension Approved' || data.status === 'Extension Rejected') && (
              <button
                onClick={handleDownload}
                disabled={downloadLoading}
                className="absolute top-4 right-[-10px] bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition disabled:opacity-60"
              >
                {downloadLoading ? 'Downloading...' : 'Download'}
              </button>
            )}

          {Number(data.created_by) === currentUserID && data.status === 'Extended' && (
            <div className="absolute top-4 right-[-150px] flex gap-3">
              <button
                onClick={handleDownload}
                disabled={downloadLoading}
                className="bg-gray-700 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-gray-800 transition disabled:opacity-60"
              >
                {downloadLoading ? 'Downloading...' : 'Download'}
              </button>

              <button
                onClick={() => setShowRemoveTOIModal(true)}
                className="bg-yellow-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-yellow-700 transition"
              >
                Revoke TOI
              </button>

              <button
                onClick={handleOpenExtensionModal}
                className="bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-blue-700 transition"
              >
                Apply for TOI Extension
              </button>
            </div>
          )}

          {Number(data.created_by) === currentUserID && data.status === 'Expired' && (
            <button
              onClick={handleDownload}
              disabled={downloadLoading}
              className="absolute top-4 right-[-10px] bg-red-600 text-white px-10 py-1 rounded-lg whitespace-nowrap shadow-md hover:bg-red-700 transition disabled:opacity-60"
            >
              {downloadLoading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>

        {/* Title */}
        <div className="mb-7">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Title</div>
          <div className="mt-1 text-lg font-semibold text-[#0D2341]">{data.title || '-'}</div>
        </div>

        {/* Details Grid */}
        <div className="mb-8">
          <div className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Site</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.site || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">TOI Number</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.toi_number || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Unit-Station</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.unit_station || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">System</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.system || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Rev</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.rev || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">HBS</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.hbs || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Serial No.</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.serial_number || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Form No.</div>
              <div className="mt-1.5 text-base font-medium leading-6 text-[#0D2341]">{data.form_number || '-'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Doc Type</div>
              <div className="mt-1.5 text-base font-medium capitalize leading-6 text-[#0D2341]">{data.doc_type || '-'}</div>
            </div>
          </div>
        </div>

        {/* Reason for Temporary Operating Instruction */}
        {data.reason_for_temporary_instruction && (
          <div className="mb-6">
            <h3 className="font-semibold text-sm mb-2">Reason for Temporary Operating Instruction</h3>

            <div
              className="prose max-w-[750px] w-full overflow-x-hidden break-words
                 [&_ul]:list-disc
                 [&_ul]:pl-6
                 [&_ol]:list-decimal
                 [&_ol]:pl-6
                 [&_li]:mb-1
                 [&_img]:max-w-full
                 [&_img]:h-auto
                 [&_table]:block
                 [&_table]:overflow-x-auto
                 [&_pre]:whitespace-pre-wrap
                 [&_pre]:break-words"
              dangerouslySetInnerHTML={{
                __html: data.reason_for_temporary_instruction,
              }}
            />
          </div>
        )}

        {/* Temporary Operating Instructions */}
        {instructions.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold mb-2">Temporary Operating Instruction</h4>

            {instructions.map((instruction, index) => (
              <div
                key={index}
                className="prose max-w-[750px] w-full overflow-x-hidden break-words
          [&_ul]:list-disc
          [&_ul]:pl-6
          [&_ol]:list-decimal
          [&_ol]:pl-6
          [&_li]:mb-1
          [&_p]:mb-2
          [&_img]:max-w-full
          [&_img]:h-auto
          [&_table]:block
          [&_table]:overflow-x-auto
          [&_pre]:whitespace-pre-wrap
          [&_pre]:break-words"
                dangerouslySetInnerHTML={{
                  __html: instruction,
                }}
              />
            ))}
          </div>
        )}

        {/* Description */}
        {data.description && (
          <div>
            <h4 className="font-semibold mb-2">Description</h4>
            <p className="text-gray-700">{data.description}</p>
          </div>
        )}
      </div>

      {/* Edit Modal - only reachable while status is New */}
      {showEditModal && (
        <TemporaryOperatingInstructionModal
          editData={data}
          onClose={() => setShowEditModal(false)}
          onSuccess={async () => {
            setShowEditModal(false);
            showSuccessToast('Temporary Operating Instruction updated successfully.');
            await fetchTOIDetails();
          }}
          onError={(message: string) => showErrorToast(message)}
        />
      )}

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Are you sure you want to delete this Temporary Operating Instruction?"
        message=""
        confirmText="Yes, Delete"
        loadingText="Deleting.."
        isLoading={deleteLoading}
        cancelText="Cancel"
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteTOI}
      />

      {/*  ACCEPT CONFIRMATION MODAL */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowAcceptModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to accept?</h3>

            <div className="flex justify-center gap-4">
              <button onClick={() => setShowAcceptModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>

              <button
                onClick={() => {
                  setShowAcceptModal(false);
                }}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {showAuthorizeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowAuthorizeModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to authorize this TOI?</h3>

            <div className="flex justify-center gap-4">
              <button onClick={() => setShowAuthorizeModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>

              <button
                onClick={handleMarkAuthorized}
                disabled={submittingAuthorize}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {submittingAuthorize ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReviewedModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[420px] p-8 relative shadow-lg">
            <button
              onClick={() => setShowReviewedModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>

            <h2 className="text-2xl font-semibold text-center text-[#0D2341] mb-8">Are you sure you want to review?</h2>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowReviewedModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleMarkReviewed}
                disabled={submittingReview}
                className="px-8 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {submittingReview ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifyShiftSupervisorModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[700px] p-8 relative shadow-lg">
            <button
              onClick={() => setShowNotifyShiftSupervisorModal(false)}
              className="absolute top-4 right-6 text-gray-400 hover:text-gray-600 text-3xl"
            >
              ×
            </button>

            <h2 className="text-lg font-bold text-[#0D2341] mb-8">Notify Shift Supervisors to accept the instructions</h2>

            <div className="mb-8">
              <label className="block text-md font-medium text-[#0D2341] mb-3">Select Shift Supervisors</label>

              <SupervisorMultiSelect
                supervisors={shiftSupervisors}
                selectedIds={selectedShiftSupervisors}
                onChange={setSelectedShiftSupervisors}
              />
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowNotifyShiftSupervisorModal(false)}
                className="px-8 py-3 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifyShiftSupervisors}
                disabled={selectedShiftSupervisors.length === 0 || isNotifyingShiftSupervisors}
                className="px-8 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isNotifyingShiftSupervisors ? <Spinner /> : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifyOperationsManagerModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[500px] p-8 relative shadow-lg">
            <button
              onClick={() => setShowNotifyOperationsManagerModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>

            <h2 className="text-xl font-semibold mb-6">Notify an Operations Manager to Authorize the Instructions</h2>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Operations Manager</label>

              <select
                value={selectedOperationsManager}
                onChange={(e) => setSelectedOperationsManager(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
              >
                <option value="">Select Operations Manager</option>

                {operationsManagers.map((manager: any) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotifyOperationsManagerModal(false)}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifyAuthorize}
                disabled={!selectedOperationsManager || isNotifyingOperationsManager}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isNotifyingOperationsManager ? <Spinner /> : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSupportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[420px] p-8 relative shadow-lg">
            <button onClick={() => setShowSupportModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl">
              ×
            </button>

            <h2 className="text-2xl font-semibold text-center text-[#0D2341] mb-8">Are you sure you want to support this TOI?</h2>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowSupportModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleMarkSupported}
                disabled={submittingSupport}
                className="px-8 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {submittingSupport ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExtensionModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[600px] max-w-[95%]">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Apply for TOI Extension</h2>

              <button onClick={() => setShowExtensionModal(false)} className="text-gray-500 hover:text-gray-700 text-xl">
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Current End Date</label>

                  <input
                    type="text"
                    value={formatDate(data.valid_to)}
                    disabled
                    className="w-full border rounded-md px-3 py-2 bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Extend To</label>

                  <StandardDatePicker value={extendTo} min={getNextDay(data.valid_to)} onChange={setExtendTo} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Select Technical Manager</label>

                <select
                  value={selectedManager}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">{loadingManagers ? 'Loading...' : 'Select Technical Manager'}</option>

                  {technicalManagers.map((manager: any) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button onClick={() => setShowExtensionModal(false)} className="px-5 py-2 border rounded-md">
                Cancel
              </button>

              <button
                onClick={handleRequestExtension}
                disabled={!selectedManager || !extendTo || isRequestingExtension}
                className="bg-red-600 text-white px-5 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isRequestingExtension ? <Spinner /> : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify to Create TOI</h3>

              <button
                onClick={() => {
                  setShowNotifyModal(false);
                  setSelectedSystemEngineer('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select System Engineer</label>

              <select
                value={selectedSystemEngineer}
                onChange={(e) => setSelectedSystemEngineer(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select System Engineer</option>

                {systemEngineer.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNotifyModal(false);
                  setSelectedSystemEngineer('');
                }}
                className="px-4 py-2 border rounded-md"
              >
                Cancel
              </button>

              <button
                onClick={handleSelectedSEngineerConfirm}
                disabled={isNotifyingSystemEngineer}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                {isNotifyingSystemEngineer ? <Spinner /> : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReviewModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[500px] p-8 relative shadow-lg">
            <button onClick={() => setShowReviewModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl">
              ×
            </button>

            <h2 className="text-xl font-semibold mb-6 text-[#0D2341]">Notify a Section Head to review the instructions</h2>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Section Head</label>

              <select
                value={selectedSectionHead}
                onChange={(e) => setSelectedSectionHead(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
              >
                <option value="">Select Section Head</option>

                {sectionHeads.map((head: any) => (
                  <option key={head.id} value={head.id}>
                    {head.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifyReview}
                disabled={!selectedSectionHead || isNotifyingSectionHead}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isNotifyingSectionHead ? <Spinner /> : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifyTechManagerModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[500px] p-8 relative shadow-lg">
            <button
              onClick={() => setShowNotifyTechManagerModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>

            <h2 className="text-xl font-semibold mb-6">Notify a Tehnical Manager to Support the instructions</h2>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Technical Manager</label>
              <select
                value={selectedTechManager}
                onChange={(e) => setSelectedTechManager(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
              >
                <option value="">Select Technical Manager</option>

                {techManagers.map((manager: any) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotifyTechManagerModal(false)}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifySupport}
                disabled={!selectedTechManager || isNotifyingTechManager}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isNotifyingTechManager ? <Spinner /> : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAcceptTOIModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowAcceptTOIModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to accept this TOI?</h3>

            <div className="flex justify-center gap-4">
              <button onClick={() => setShowAcceptTOIModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>

              <button
                onClick={handleAcceptTOI}
                disabled={acceptTOILoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {acceptTOILoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showApproveExtensionModal &&
        (() => {
          const pendingExtension = data?.extensions?.find(
            (ext) => Number(ext.approved_by?.id) === Number(currentUserID) && ext.status === 'PENDING',
          );

          return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-[500px] p-8 shadow-lg">
                <h3 className="text-xl font-semibold text-center text-[#0D2341] mb-8">
                  Are you sure you want to Extend TOI
                  <br />
                  from {formatDate(data.valid_to)} to{' '}
                  {pendingExtension?.requested_end_date ? formatDate(pendingExtension.requested_end_date) : 'selected date'}?
                </h3>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowApproveExtensionModal(false)}
                    disabled={approveExtensionLoading}
                    className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleApproveExtension}
                    disabled={approveExtensionLoading}
                    className="px-8 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {approveExtensionLoading ? (
                      <>
                        <Spinner />
                        Processing...
                      </>
                    ) : (
                      'Yes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      {showRejectExtensionModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[500px] p-8 shadow-lg">
            <h3 className="text-xl font-semibold text-center text-[#0D2341] mb-8">
              Are you sure you want to reject this TOI extension request?
            </h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowRejectExtensionModal(false)}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button onClick={handleRejectExtension} className="px-8 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemoveTOIModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[500px] p-8 shadow-lg relative">
            <button
              onClick={() => setShowRemoveTOIModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>

            <h3 className="text-xl font-semibold text-center text-[#0D2341] mb-8">Are you sure you want to revoke this TOI?</h3>

            <div className="flex justify-center gap-4">
              <button onClick={() => setShowRemoveTOIModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>

              <button
                onClick={handleRemoveTOI}
                disabled={removingTOI}
                className="px-8 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {removingTOI ? 'Revoking...' : 'Yes, Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tempOperatingInstructionModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[800px] flex flex-col shadow-lg">
            <div className="p-6 flex-1">
              <h2 className="font-semibold mb-4">Add Temporary Operating Instruction</h2>
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                onClick={() => setTempOperatingInstructionModal(false)}
              >
                ✕
              </button>

              <label className="block mb-2">Temporary Instructions</label>

              <ReactQuill
                value={newInstruction}
                onChange={(value) => {
                  if (stripHtml(value).length <= 2000) {
                    setNewInstruction(value);
                  }
                }}
                placeholder="Add temporary instruction here"
              />

              <div className={`text-xs mt-1 text-right ${stripHtml(newInstruction).length >= 2000 ? 'text-red-500' : 'text-gray-400'}`}>
                {stripHtml(newInstruction).length}/2000
              </div>
            </div>

            <div className="border-t p-4 flex justify-end gap-3">
              <button className="px-4 py-2 border rounded-md" onClick={() => setTempOperatingInstructionModal(false)}>
                Cancel
              </button>

              <button
                onClick={handleAddInstruction}
                disabled={addInstructionLoading || stripHtml(newInstruction).length > 2000}
                className={`px-4 py-2 rounded-md text-white ${
                  addInstructionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {addInstructionLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessMessage && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg px-5 py-4 min-w-[320px]">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">✓ Notification Sent</h4>

                <p className="text-sm mt-1">{successMessage}</p>
              </div>

              <button onClick={() => setShowSuccessMessage(false)} className="ml-4 text-white">
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorMessage && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg px-5 py-4 min-w-[340px]">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">✕ Error</h4>

                <p className="text-sm mt-1">{errorMessage}</p>
              </div>

              <button onClick={() => setShowErrorMessage(false)} className="ml-4 text-white">
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TempOperatingInstructionDetailsPage;
